import React from 'react';
import { ChevronLeft, ChevronRight, QrCode, Layers, PowerOff, Trash2 } from 'lucide-react';
import C from '../../constants/colors';
import StatusDot from '../ui/StatusDot';
import { RealQRCode, qrEncodeToCanvas } from '../../utils/qr';

function formatTimeAgo(isoString) {
  if (!isoString) return 'Unknown';
  const diff = Date.now() - new Date(isoString).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60)  return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60)  return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)  return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function ZoneView({ zone, zones, site, company, employees, visitorCards = [], onBack, onEditZone, onAddZone, onSubZoneClick, onDeactivate, onDeleteZone, user }) {
  const zoneEmployees = employees.filter(e => e.currentZoneId === zone.id);
  const zoneVisitors = visitorCards.filter(vc => vc.currentZoneId === zone.id && vc.status === 'ASSIGNED');
  const totalOccupants = zoneEmployees.length + zoneVisitors.length;
  const subZones = (zones || []).filter(z => z.parentZoneId === zone.id);
  const occupancy = zone.capacity > 0 ? Math.round((totalOccupants / zone.capacity) * 100) : 0;

  async function handlePrintQR() {
    const qrSize = 200;
    const qrCanvas = document.createElement('canvas');
    await qrEncodeToCanvas(window.location.origin + '/?z=' + zone.qrHash, qrCanvas, qrSize);

    const out = document.createElement('canvas');
    const ctx = out.getContext('2d');
    out.width = qrSize + 40;
    out.height = qrSize + 80;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, out.width, out.height);
    ctx.drawImage(qrCanvas, 20, 20);
    ctx.fillStyle = '#000';
    ctx.font = 'bold 14px DM Sans, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(zone.name, out.width / 2, qrSize + 44);
    ctx.font = '11px DM Sans, sans-serif';
    ctx.fillStyle = '#555';
    ctx.fillText(site.name + (zone.type !== 'MOBILE' ? ' · Floor ' + zone.floor : ''), out.width / 2, qrSize + 62);

    const win = window.open('', '_blank');
    if (win) {
      win.document.write('<html><body style="margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;background:#f5f5f5;"><img src="' + out.toDataURL() + '" style="box-shadow:0 2px 8px rgba(0,0,0,0.2);"/></body></html>');
      win.document.close();
      setTimeout(() => win.print(), 500);
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}`, flexShrink: 0, background: C.surface }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
          <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, display: 'flex', alignItems: 'center', gap: '4px' }}>
            <ChevronLeft size={16} /><span style={{ fontSize: '13px' }}>{site.name}</span>
          </button>
          <ChevronRight size={14} color={C.textMuted} />
          <span style={{ fontSize: '13px', color: C.textPrimary, fontWeight: 600 }}>{zone.name}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          {zone.type === 'MOBILE' && (
            <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '6px', background: C.amber + '22', color: C.amber, border: `1px solid ${C.amber}44` }}>Mobile</span>
          )}
          {!zone.isActive && (
            <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '6px', background: C.textMuted + '22', color: C.textMuted, border: `1px solid ${C.textMuted}44` }}>Inactive</span>
          )}
          <span style={{ fontSize: '12px', color: C.textMuted }}>
            {zone.type !== 'MOBILE' && `Floor ${zone.floor} · `}Capacity: {zone.capacity} &nbsp;·&nbsp; {company?.name}
          </span>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
          {/* QR Code card */}
          <div style={{ background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '14px', padding: '24px', textAlign: 'center', minWidth: '220px' }}>
            <div style={{ fontSize: '12px', color: C.textMuted, marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Zone QR Code</div>
            <div style={{ background: '#fff', borderRadius: '10px', padding: '12px', display: 'inline-block', marginBottom: '12px' }}>
              <RealQRCode value={window.location.origin + '/?z=' + zone.qrHash} size={160} darkColor='#0a0e17' lightColor='#ffffff' />
            </div>
            <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '13px', color: C.textSecondary, marginBottom: '4px' }}>{zone.qrHash}</div>
            <div style={{ fontSize: '12px', color: C.textMuted, marginBottom: '16px', wordBreak: 'break-all' }}>?z={zone.qrHash}</div>
            <button onClick={handlePrintQR} style={{ width: '100%', padding: '9px', borderRadius: '8px', border: `1px solid ${C.blue}44`, background: C.blue+'22', color: C.blue, fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
              <QrCode size={14} />Print Zone QR
            </button>
            <button onClick={onEditZone} style={{ width: '100%', marginTop: '8px', padding: '9px', borderRadius: '8px', border: `1px solid ${C.border}`, background: 'transparent', color: C.textSecondary, fontSize: '13px', cursor: 'pointer', fontFamily: 'inherit' }}>Edit Zone</button>
            <button onClick={onAddZone} style={{ width: '100%', marginTop: '8px', padding: '9px', borderRadius: '8px', border: `1px solid ${C.green}44`, background: C.green+'11', color: C.green, fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
              <Layers size={14} />+ Add Zone
            </button>
            {user?.role === 'ADMIN' && zone.isActive && (
              <button onClick={onDeactivate} style={{ width: '100%', marginTop: '8px', padding: '9px', borderRadius: '8px', border: `1px solid ${C.amber}44`, background: C.amber+'11', color: C.amber, fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                <PowerOff size={14} />Deactivate Zone
              </button>
            )}
            {user?.role === 'ADMIN' && !zone.isActive && (
              <button onClick={onDeleteZone} style={{ width: '100%', marginTop: '8px', padding: '9px', borderRadius: '8px', border: `1px solid ${C.red}44`, background: C.red+'11', color: C.red, fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                <Trash2 size={14} />Delete Zone
              </button>
            )}
          </div>

          {/* Employee + Visitor list */}
          <div style={{ flex: 1, minWidth: '280px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
              <h3 style={{ fontSize: '15px', fontWeight: 700, color: C.textPrimary, margin: 0 }}>
                People in Zone
                <span style={{ fontSize: '13px', fontWeight: 400, color: C.textMuted, marginLeft: '8px' }}>({totalOccupants}/{zone.capacity || '∞'})</span>
              </h3>
              <div style={{ fontSize: '12px', color: occupancy > 80 ? C.red : C.green }}>{occupancy}% capacity</div>
            </div>

            {zone.description && (
              <div style={{ background: C.surfaceElevated, borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '13px', color: C.textSecondary, border: `1px solid ${C.border}` }}>
                {zone.description}
              </div>
            )}

            {zoneEmployees.length === 0 && zoneVisitors.length === 0 ? (
              <div style={{ textAlign: 'center', color: C.textMuted, padding: '32px 0', fontSize: '13px' }}>No one currently in this zone</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {zoneEmployees.map(emp => (
                  <div key={emp.id} style={{ background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <StatusDot status={emp.status} size={10} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: C.textPrimary }}>{emp.name}</div>
                      <div style={{ fontSize: '11px', color: C.textMuted }}>{emp.employeeId} · {emp.department}</div>
                    </div>
                    <div style={{ fontSize: '11px', color: C.textMuted }}>{emp.lastUpdate ? formatTimeAgo(emp.lastUpdate) : '—'}</div>
                  </div>
                ))}
                {zoneVisitors.map(vc => (
                  <div key={vc.id} style={{ background: C.surfaceElevated, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <StatusDot status={vc.emergencyStatus} size={10} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: C.textPrimary }}>{vc.visitorName || vc.cardNumber}</div>
                      <div style={{ fontSize: '11px', color: C.textMuted }}>
                        {vc.cardNumber}
                        {vc.visitorCompanyName ? ` · ${vc.visitorCompanyName}` : ''}
                        {' · '}<span style={{ color: C.blue, fontWeight: 600 }}>Visitor</span>
                      </div>
                    </div>
                    <div style={{ fontSize: '11px', color: C.textMuted }}>{vc.lastUpdate ? formatTimeAgo(vc.lastUpdate) : '—'}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Sub-zones */}
            {subZones.length > 0 && (
              <div style={{ marginTop: '24px' }}>
                <div style={{ fontSize: '12px', color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>Sub-zones ({subZones.length})</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {subZones.map(sub => {
                    const subEmps = employees.filter(e => e.currentZoneId === sub.id);
                    const subVis = visitorCards.filter(vc => vc.currentZoneId === sub.id && vc.status === 'ASSIGNED');
                    const subTotal = subEmps.length + subVis.length;
                    const subNeedHelp = subEmps.filter(e => e.status === 'NEED_HELP').length + subVis.filter(vc => vc.emergencyStatus === 'NEED_HELP').length;
                    return (
                      <div key={sub.id} onClick={() => onSubZoneClick && onSubZoneClick(sub.id)}
                        style={{ background: C.surfaceElevated, border: `1px solid ${subNeedHelp > 0 ? C.red : C.border}`, borderRadius: '10px', padding: '12px 14px', cursor: onSubZoneClick ? 'pointer' : 'default', display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Layers size={14} color={C.blue} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '13px', fontWeight: 600, color: C.textPrimary }}>{sub.name}</div>
                          <div style={{ fontSize: '11px', color: C.textMuted }}>Floor {sub.floor} · {subTotal}/{sub.capacity || '∞'} occupants</div>
                        </div>
                        {subNeedHelp > 0 && <span style={{ background: C.red, color: '#fff', fontSize: '10px', fontWeight: 700, padding: '2px 6px', borderRadius: '8px' }}>{subNeedHelp}</span>}
                        <ChevronRight size={14} color={C.textMuted} />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
